import { ChangeDetectorRef, Component, Directive, ElementRef, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { Router } from "@angular/router";
import { Plugins, SharePluginWeb } from "@capacitor/core";
import { ActionSheetController, IonicSlides } from "@ionic/angular";
import { Subscription } from "rxjs";
import SwiperCore from "swiper";
import { Viewable } from "../../components/bit-cover/bit-cover.component";
import { AnalyticsService } from "../../services/analytics.service";
import { ChapterService } from "../../services/chapters.service";
import { FirebaseService } from "../../services/firebase.service";
import { HelperService } from "../../services/helper.service";
import { UserService } from "../../services/user.service";
import { Profile } from "../profile/profile.page";
import { StoreCollection } from "../store/store.page";
import { RemoteConfigService } from "./../../services/remote-config.service";

export enum BIT_TYPES {
  "article" = "article",
  "book" = "book",
  "chapter" = "chapter",
  "essay" = "essay",
  "handout" = "handout",
  "instructions" = "instructions",
  "jokes" = "jokes",
  "letter" = "letter",
  "lyrics" = "lyrics",
  "manual" = "manual",
  "memoir" = "memoir",
  "poem" = "poem",
  "recipe" = "recipe",
  "story" = "story",
  "worksheet" = "worksheet",
  "other" = "other",
}

export enum BIT_TYPES_MAP {
  "article" = "Article",
  "book" = "Book",
  "chapter" = "Chapter",
  "essay" = "Essay",
  "handout" = "Handout",
  "instructions" = "Instructions",
  "jokes" = "Jokes",
  "letter" = "Letter",
  "lyrics" = "Lyrics",
  "manual" = "Manual",
  "memoir" = "Memoir",
  "poem" = "Poem",
  "recipe" = "Recipe",
  "story" = "Story",
  "worksheet" = "Worksheet",
  "other" = "Other",
}

// export enum BIT_TOPICS {
//   "arts_architecture" = "Arts & Architecture",
//   "business_economics" = "Business & Economics",
//   "collaboration" = "Collaboration",
//   "computers_technology" = "Computers & Technology",
//   "education_teaching" = "Education & Teaching",
//   "fiction" = "Fiction",
//   "history_archeology" = "History & Archeology",
//   "music_performing_arts" = "Music & Performing Arts",
//   "non_fiction" = "Non-Fiction",
//   "organizational_development" = "Organizational Development, Change and Behavior",
//   "parenting_family_relationships" = "Parenting, Family & Relationships",
//   "political_social_science" = "Political & Social Science",
//   "psychology" = "Psychology",
//   "other" = "Other",
// }
@Directive({
  selector: "[appVisible]",
})
export class VisibleDirective implements OnInit, OnDestroy {
  @Output() visible = new EventEmitter();
  private observer?: IntersectionObserver;

  constructor(private el: ElementRef) {}

  ngOnInit() {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.visible.emit("");
          this.observer?.unobserve(this.el.nativeElement);
        }
      },
      { threshold: 0.2 },
    );
    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}

SwiperCore.use([IonicSlides]);
@Component({
  selector: "app-know-bits",
  templateUrl: "./know-bits.page.html",
  styleUrls: ["./know-bits.page.scss"],
})
export class KnowBitsPage implements OnInit, OnDestroy {
  shareLink: string = "https://app.mylibrary.world/content";

  BIT_TYPES = this.remoteConfigServ.BIT_TYPES;
  BIT_TYPES_MAP = this.remoteConfigServ.BIT_TYPES_MAP;
  BIT_TOPICS = Object.entries(this.remoteConfigServ.BIT_TOPICS);
  LENGTH_OF_POPULAR: number = 20;
  INITIAL_ROWS: number = 2;
  loadedTopics: Record<string, boolean> = {};

  freeContent: StoreCollection[] = [];

  subscriptions: Subscription[] = [];
  chaptersSub: Subscription;

  swipers: (SwiperCore & { topic: string })[] = [];

  searchTerm: string = "";
  searching: boolean = false;

  knowBitTopics: { [topic: string]: { bits: Viewable[]; views: number } } = {}; // all BIT_TOPICS with Popular and Collections
  topicsAsArray: { topic: string; bits: Viewable[]; views: number }[] = [
    { bits: Array(15).fill({}), topic: "Popular", views: -1 },
    { bits: Array(15).fill({}), topic: "Collections", views: -1 },
    ...this.BIT_TOPICS.map((key) => {
      return { topic: key[1], bits: Array(15).fill({}), views: -1 };
    }),
  ];

  signedIn: boolean = false;

  profile: Profile;

  loading: boolean = true;

  constructor(
    private fireServ: FirebaseService,
    private router: Router,
    private helperServ: HelperService,
    private chapServ: ChapterService,
    private actionSheetCtrl: ActionSheetController,
    private analytics: AnalyticsService,
    private userServ: UserService,
    public remoteConfigServ: RemoteConfigService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.destorySubs();
  }

  async ngOnInit() {
    this.getContent();

    this.watchUser();
    this.loading = false;
  }

  // initBitTypes() {
  //   this.knowBitTopics = {};
  //   Object.values(this.remoteConfigServ.BIT_TOPICS).forEach((topic: BIT_TOPICS) => {
  //     this.knowBitTopics[topic] = {}
  //   });

  //   this.knowBitTopics["Popular"] = [];
  //   this.knowBitTopics["Collections"] = [];
  // }

  destorySubs() {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  watchUser() {
    const sub = this.userServ.$userProfile.subscribe((profile) => {
      this.signedIn = profile ? true : false;
      this.profile = profile;
    });

    this.subscriptions.push(sub);
  }

  getBits(types: [string, string]) {
    const start = Date.now();
    this.loadedTopics[types[1]] = true;
    return new Promise((resolve, reject) => {
      this.fireServ
        .getBits(
          [
            ["topics", "array-contains", types[0]],
            ["status", "==", "public"],
            ["active", "==", true],
          ],
          [
            // ["price", "==", 0],
            ["active", "==", true],
          ],
        )
        .subscribe(
          (data) => {
            if (data.length == 0) {
              return;
            }
            const bits = data.map((bit) => {
              return { ...bit, startingId: bit.id as string, creators: bit.authors as string[], views: bit.viewCount } as Viewable;
            });
            this.knowBitTopics[types[1]] = { bits: bits, views: 0 };
            // });
          },
          () => {},
          () => {
            const foundIndex = this.topicsAsArray.findIndex((topic) => topic.topic == types[1]);
            if (this.knowBitTopics[types[1]] == null && foundIndex != -1) {
              this.loadRow(""); // triggers to load next row if current row had no elements
              this.topicsAsArray[foundIndex].views = -2;
            } else if (foundIndex != -1) {
              this.topicsAsArray[foundIndex].bits = this.knowBitTopics[types[1]].bits;
              this.topicsAsArray[foundIndex].views = this.knowBitTopics[types[1]].views;
              setTimeout(() => {
                this.swipers
                  .find((swiper) => {
                    return swiper.topic == types[1];
                  })
                  .update();
              }, 0);
            }
            this.changeDetector.detectChanges();
            console.log(types + " resolved.", (Date.now() - start) / 1000);
            resolve(true);
          },
        );
    });
  }

  async getCollections() {
    const start = Date.now();
    return new Promise((resolve, reject) => {
      this.fireServ
        .getLibraries([
          // ["price", "==", 0],
          ["volumes", "==", true],
          ["active", "==", true]
        ])
        .subscribe(
          (data) => {
            this.knowBitTopics["Collections"] = {
              bits: data.sort((a, b) => {
                // if (a.views == b.views) {
                return b.avgRating - a.avgRating;
                // } else {
                //   return b.views - a.views;
                // }
              }),
              views: data.reduce((prev, current) => {
                return prev + current.views;
              }, 0),
            };
          },
          () => {},
          () => {
            this.topicsAsArray[1].bits = this.knowBitTopics["Collections"].bits;
            this.topicsAsArray[1].views = this.knowBitTopics["Collections"].views;
            this.changeDetector.detectChanges();
            setTimeout(() => {
              this.swipers
                .find((swiper) => {
                  return swiper.topic == "Collections";
                })
                .update();
            }, 0);
            console.log("Collections resolved.", (Date.now() - start) / 1000);
            resolve(true);
          },
        );
    });
  }

  async getPopular() {
    const start = Date.now();
    return new Promise((resolve, reject) => {
      this.fireServ.getPopular().subscribe(
        (data) => {
          this.knowBitTopics["Popular"] = {
            bits: data.map((item) => {
              return { ...item, startingId: item.id as string } as Viewable;
            }),
            views: Number.MAX_VALUE,
          };
        },
        () => {},
        () => {
          this.topicsAsArray[0].bits = this.knowBitTopics["Popular"].bits;
          this.topicsAsArray[0].views = this.knowBitTopics["Popular"].views;

          this.changeDetector.detectChanges();
          setTimeout(() => {
            this.swipers
              .find((swiper) => {
                return swiper.topic == "Popular";
              })
              .update();
          }, 0);
          console.log("Popular resolved.", (Date.now() - start) / 1000);
          resolve(true);
        },
      );
    });
  }

  async getContent() {
    const start = Date.now();
    Promise.all([
      this.getPopular(),
      this.getCollections(),
      this.BIT_TOPICS.slice(0, this.INITIAL_ROWS).forEach((entry) => {
        this.getBits(entry);
      }),
    ]).then(() => {
      console.log("Initial load complete", (Date.now() - start) / 1000);
    });
  }

  trackType(index: number, item: { topic: string; bits: Viewable[]; views: number }) {
    return item.topic;
  }

  trackBit(index: number, item: Viewable) {
    return index;
  }

  async uploadEpub() {
    await this.userServ.uploadEpub();
  }

  onSwiper(swiper: SwiperCore, topic: string) {
    swiper["topic"] = topic;
    this.swipers.push(swiper as SwiperCore & { topic: string });
  }

  search() {
    console.log(this.searchTerm);
    if (!this.searchTerm || this.searchTerm.length < 3) {
      this.helperServ.presentToast("Please enter 3 or more characters");
      return;
    }

    this.router.navigate(["search-results"], { queryParams: { search_query: this.searchTerm } });
  }

  nextSlide(topic: string) {
    const foundIndex = this.swipers.findIndex((swiper) => swiper["topic"] == topic);
    if (foundIndex != -1) {
      this.swipers[foundIndex].slideNext();
    }
  }

  prevSlide(topic: string) {
    const foundIndex = this.swipers.findIndex((swiper) => swiper["topic"] == topic);
    if (foundIndex != -1) {
      this.swipers[foundIndex].slidePrev();
    }
  }

  async share() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: "Share the app?",
      mode: "ios",
      //subHeader: copyLink,
      buttons: [
        {
          text: "Copy Link",
          handler: () => {
            //// Different Browser capabilties
            if (window.navigator.userAgent.includes("Chrome")) {
              Plugins.Clipboard.write({ url: this.shareLink })
                .then((value) => {
                  this.copiedMessage("Successfully copied the link to your clipboard!");
                  this.analytics.event_share({ share_method: "link", link: this.shareLink });
                })
                .catch((error) => {
                  console.log(error);
                  this.copiedMessage("Something went wrong. Please try again.");
                });
            } else {
              // Create new element
              var el = document.createElement("textarea");
              // Set value (string to be copied)
              el.value = this.shareLink;
              // Set non-editable to avoid focus and move outside of view
              el.setAttribute("readonly", "");
              //el.style = {position: 'absolute', left: '-9999px'};
              document.body.appendChild(el);
              // Select text inside element

              el.select();
              // Copy text to clipboard
              if (document.execCommand("copy").valueOf()) {
                this.copiedMessage("Successfully copied the link to your clipboard!");
                this.analytics.event_share({ share_method: "link", link: this.shareLink });
              } else {
                this.copiedMessage("Something went wrong. Please try again.");
              }
              // Remove temporary element
              document.body.removeChild(el);
            }
          },
        },
        {
          text: "Share via ... (for mobile & some browsers)",
          handler: () => {
            this.presentShareOptions();
          },
        },
        {
          text: "Cancel",
          role: "cancel",
        },
      ],
    });

    await actionSheet.present();
  }

  async presentShareOptions() {
    await SharePluginWeb.prototype
      .share({
        title: "See cool stuff",
        text: "myLibrary",
        url: this.shareLink,
        dialogTitle: "Share with buddies",
      })
      .then(() => {
        this.analytics.event_share({ share_method: "Native options", link: this.shareLink });
      })
      .catch((error) => {
        console.log(error, "Dang it");
      });
  }

  async copiedMessage(message: string) {
    this.helperServ.presentToast(message, false, true, 5000);
  }

  async handleRefresh(event) {
    this.destorySubs();
    this.getContent();
    this.watchUser();
    event.target.complete();
  }

  loadRow(topic: string) {
    if (this.loadedTopics[topic] || !topic || topic == "Popular" || topic == "Collections") {
      return;
    }
    this.getBits(this.BIT_TOPICS.find((topics) => topics[1] == topic));
  }
}
